import { Box, createStyles, Group, Stack } from '@mantine/core';
import { IconButton, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { ExpandCollapse } from '../../../components/ExpandCollapse/ExpandCollapse';
import { RichEditor } from '../../../components/RichEditor';
import type { CreatePolicyStore } from './CreatePolicyStore';
import {
	PiiClassifierOptions,
	type PiiClassifierOption,
} from './PiiClassifierOptions';

const useStyles = createStyles((theme) => ({
	root: {
		borderRadius: theme.radius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		gap: 0,
	},
	textStack: {
		gap: 0,
	},
	input: {
		width: '100%',
		minHeight: theme.other.space[24],
		borderColor: theme.other.getColor('border/input/default'),
		padding: `${theme.spacing['2xs']} ${theme.spacing.sm}`,
		borderRadius: theme.radius.sm,
		borderWidth: 0.5,
		borderStyle: 'solid',
		backgroundColor: theme.other.getColor('surface/input/default'),
		'&:has(.ProseMirror-focused)': {
			boxShadow: theme.shadows.sm,
		},
	},
	piiClassifierOptionGroup: {
		gap: theme.spacing.sm,
	},
	editor: {
		'& .ProseMirror': {
			wordBreak: 'break-word',
			fontSize: theme.fontSizes.sm,
			minHeight: theme.other.space[24],
		},
	},
	contentRoot: {
		padding: theme.spacing.md,
		gap: theme.spacing.sm,
	},
	instructionsStack: {
		gap: theme.spacing['3xs'],
	},
}));

export const PiiClassifierStack = observer(
	({
		store,
		index,
		removePolicyRule,
	}: {
		store: CreatePolicyStore;
		index: number;
		removePolicyRule: (index: number, isPIIClassifier?: boolean) => void;
	}) => {
		const { theme, classes } = useStyles();

		const handleInstructionsChange = useCallback(
			(value: string | undefined) => {
				store.setPIIClassifierInstructions(index, value ?? '');
			},
			[store, index]
		);

		const PII_CLASSIFIER_LEFT_SIDE_OPTIONS: PiiClassifierOption[] = [
			{
				label: 'Medical Information',
				value: 'PII.MEDICAL',
				checked:
					!!store.policyRules[index].classifiers?.includes('PII.MEDICAL'),
			},
			{
				label: 'Contact Information',
				value: 'PII.CONTACT',
				checked:
					!!store.policyRules[index].classifiers?.includes('PII.CONTACT'),
			},
			{
				label: 'Identity Information',
				value: 'PII.IDENTITY',
				checked:
					!!store.policyRules[index].classifiers?.includes('PII.IDENTITY'),
			},
		];

		const PII_CLASSIFIER_RIGHT_SIDE_OPTIONS: PiiClassifierOption[] = [
			{
				label: 'Financial Information',
				value: 'PII.FINANCIAL',
				checked:
					!!store.policyRules[index].classifiers?.includes('PII.FINANCIAL'),
			},
			{
				label: 'Online Information',
				value: 'PII.ONLINE',
				checked: !!store.policyRules[index].classifiers?.includes('PII.ONLINE'),
			},
			{
				label: 'ID Numbers',
				value: 'PII.ID_NUMBERS',
				checked:
					!!store.policyRules[index].classifiers?.includes('PII.ID_NUMBERS'),
			},
		];

		return (
			<Stack className={classes.root}>
				<Box
					px="md"
					bg={theme.other.getColor('surface/secondary/default')}
					sx={{
						borderTopLeftRadius: theme.radius.md,
						borderTopRightRadius: theme.radius.md,
					}}
				>
					<Group position="apart" py="md">
						<Stack className={classes.textStack}>
							<Text size="sm" weight="semibold">
								PII classifier
							</Text>
							<Text size="sm" color="text/secondary/default">
								Automatically scans and identifies sensitive fields (note: scans
								sample of data).
							</Text>
						</Stack>
						<IconButton
							variant="tertiary"
							size="sm"
							iconName="x"
							tooltip="Remove rule"
							onClick={() => removePolicyRule(index, true)}
						/>
					</Group>
				</Box>
				<Stack className={classes.contentRoot}>
					<Group className={classes.piiClassifierOptionGroup}>
						<PiiClassifierOptions
							index={index}
							store={store}
							options={PII_CLASSIFIER_LEFT_SIDE_OPTIONS}
						/>
						<PiiClassifierOptions
							index={index}
							store={store}
							options={PII_CLASSIFIER_RIGHT_SIDE_OPTIONS}
						/>
					</Group>
					<Stack className={classes.instructionsStack}>
						<Group spacing="3xs">
							<Text weight="semibold">Custom detection instructions</Text>
							<Text size="sm" color="text/secondary/default">
								(Optional)
							</Text>
						</Group>
						<Box className={classes.input}>
							<ExpandCollapse maxHeight={200} enabled>
								<RichEditor
									readOnly={false}
									initialValue={store.policyRules[index].llm_instructions ?? ''}
									onChangeCallback={handleInstructionsChange}
									disableTopGap
									limited
									autoFocus={false}
									dataTestId="pii-classifier-instructions"
									className={classes.editor}
								/>
							</ExpandCollapse>
						</Box>
						<Text size="sm" color="text/secondary/default">
							Write additional instructions to help the AI detect custom PII
							patterns (e.g. internal employee IDs). This is optional and only
							used with AI-based scanning
						</Text>
					</Stack>
				</Stack>
			</Stack>
		);
	}
);
